import {
  Box, Button, Card, CardContent, Chip, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { patient } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { ConsentReceipt, VerifiableHealthCredential } from '@/types/medical';

/**
 * Wallet.
 *
 * Two tabs:
 *   1. Credentials — verifiable credentials issued to the patient
 *      (e.g. immunization records, ID-verified attestations).
 *   2. Receipts — every consent receipt the platform has minted on the
 *      patient's behalf. Each receipt has a "Verify" button that calls
 *      the backend's HMAC verification (services/consent.verify_receipt_hash).
 *
 * The verify result is stored in component state per receipt so the user
 * sees the badge immediately.
 */

type TabKey = 'credentials' | 'receipts';

export function PatientWalletPage() {
  const [tab, setTab] = useState<TabKey>('credentials');

  return (
    <>
      <PageHeader
        title="Credentials wallet"
        subtitle="Your verifiable credentials and signed consent receipts."
      />
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="credentials" label="Credentials" icon={<VerifiedUserIcon />} iconPosition="start" />
        <Tab value="receipts"    label="Consent receipts" icon={<ReceiptLongIcon />} iconPosition="start" />
      </Tabs>

      {tab === 'credentials' ? <CredentialsTab /> : <ReceiptsTab />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CredentialsTab() {
  const q = useApiQuery({
    queryKey: ['patient', 'wallet', 'credentials'],
    queryFn:  patient.wallet.credentials.list,
  });

  return (
    <DataState
      query={q}
      empty={{
        title: 'No credentials yet',
        description: 'Verifiable credentials (e.g. immunization records, ID-verified attestations) appear here once issued.',
      }}
    >
      {(items) => (
        <Stack spacing={1.5}>
          {items.map((c) => <CredentialRow key={c.id} cred={c} />)}
        </Stack>
      )}
    </DataState>
  );
}

function CredentialRow({ cred }: { cred: VerifiableHealthCredential }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: cred.is_active_now ? 'success.main' : 'text.disabled',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <VerifiedUserIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap
                          sx={{ textTransform: 'capitalize' }}>
                {cred.credential_type.replace(/_/g, ' ')}
              </Typography>
              <Chip
                size="small"
                label={cred.status}
                color={cred.is_active_now ? 'success' : 'default'}
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              issued by {cred.issuer_name}
            </Typography>
            <Typography variant="caption" color="text.secondary"
                        className="tabular">
              issued {formatDateTime(cred.issued_at)}
              {cred.valid_to && ` · expires ${formatDateTime(cred.valid_to)}`}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ReceiptsTab() {
  const q = useApiQuery({
    queryKey: ['patient', 'wallet', 'receipts'],
    queryFn:  patient.wallet.receipts.list,
  });

  return (
    <DataState
      query={q}
      empty={{
        title: 'No receipts yet',
        description: 'Each time you give or revoke consent, a signed receipt is created and shown here.',
      }}
    >
      {(items) => (
        <Stack spacing={1.5}>
          {items.map((r) => <ReceiptRow key={r.id} receipt={r} />)}
        </Stack>
      )}
    </DataState>
  );
}

function ReceiptRow({ receipt }: { receipt: ConsentReceipt }) {
  const [result, setResult] = useState<'unknown' | 'valid' | 'invalid'>('unknown');
  const qc = useQueryClient();
  const toast = useToast();

  const verifyMut = useApiMutation({
    mutationFn: () => patient.wallet.receipts.verify(receipt.id),
    onSuccess: (data) => {
      setResult(data.valid ? 'valid' : 'invalid');
      if (!data.valid) toast.warning('Receipt failed verification.');
      qc.invalidateQueries({ queryKey: ['patient', 'wallet', 'receipts'] });
    },
    onError: (e) => {
      setResult('invalid');
      toast.error(e.message);
    },
  });

  const isRevocation =
    typeof receipt.scope === 'object' &&
    (receipt.scope as { op?: string }).op === 'revoke';

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ReceiptLongIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap
                          sx={{ textTransform: 'capitalize' }}>
                {receipt.purpose.replace(/_/g, ' ')}
              </Typography>
              {isRevocation && (
                <Chip size="small" label="revocation" variant="outlined" />
              )}
              {result === 'valid' && (
                <Chip size="small"
                      icon={<CheckCircleIcon fontSize="small" />}
                      label="verified" color="success" />
              )}
              {result === 'invalid' && (
                <Chip size="small"
                      icon={<ErrorOutlineIcon fontSize="small" />}
                      label="failed" color="error" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" className="tabular">
              issued {formatDateTime(receipt.issued_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary"
                        className="tabular"
                        sx={{ display: 'block', wordBreak: 'break-all' }}>
              hash: {receipt.receipt_hash.slice(0, 32)}…
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={verifyMut.isPending}
            onClick={() => verifyMut.mutate()}
          >
            Verify
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
